<template>
	<spacetimator v-model="rooms" :recomendations="recomendations" :items="items" :spaces="spaces" :units="units">
		<div slot-scope="{ items, spaces, rooms, recommendedSpaces, addRoom, removeRoom, addItem, removeItem, changeQty, roomVolume, totalVolume, addRoomAttrs, addRoomEvents, addItemAttrs, addItemEvents, changeQtyAttrs, changeQtyEvents }">
			<div else>
				<div class="st-flex st-mb-4">
					<input class="st-border st-rounded-l st-w-3/4 st-py-2 st-px-3" :class="[textDarker]" placeholder="Room Name" v-bind="addRoomAttrs" v-on="addRoomEvents">
					<button class="st-font-light st-w-1/4 st-py-2 st-px-4 st-rounded-r" :class="[primaryButtonBg, primaryButtonBgHover, primaryButtonText]" @click="addRoom">Add Room</button>
				</div>
				<slide-down-transition-group>
					<div class="st-rounded st-shadow st-p-4 st-mb-4" :class="[bgLightest]" :key="room.name" v-for="room in rooms">
						<div class="st-flex st-items-end st-justify-between st-mb-2" :class="[textDark]">
							<div class="st-text-lg st-flex-1">
								{{ room.name }}
								<button class="st-p-1 st-font-light st-text-base st-bg-transparent hover:st-bg-transparent st-focus:bg-transparent st-text-gray-300 hover:st-text-red-500" @click="removeRoom(room)">
									<font-awesome-icon :icon="times" />
								</button>
							</div>
							<div class="st-text-xs">
								<div>{{ room.volume[units] }}<span v-text="units === 'metric' ? 'm' : 'ft'"></span><sup>3</sup></div>
							</div>
						</div>
						<slide-down-transition>
							<div v-show="room.items.length > 0">
								<div class="st-flex st-items-center st-border-b st-border-gray-300">
									<div class="st-px-2 st-py-1 st-w-12 st-text-center st-text-base st-font-light st-border-0" :class="[textDark]">&nbsp;</div>
									<div class="st-px-2 st-py-1 st-w-3/4 st-text-left st-text-base st-font-light st-border-0" :class="[textDark]">Item</div>
									<div class="st-px-2 st-py-1 st-w-24 st-text-center st-text-base st-font-light st-border-0" :class="[textDark]">Volume</div>
									<div class="st-px-2 st-py-1 st-w-24 st-text-center st-text-base st-font-light st-border-0" :class="[textDark]">Quantity</div>
									<div class="st-px-2 st-py-1 st-w-24 st-text-center st-text-base st-font-light st-border-0" :class="[textDark]">Total</div>
								</div>
							</div>
						</slide-down-transition>
						<slide-down-transition-group>
							<div class="st-flex st-items-center st-border-b st-border-gray-300" :key="item.label" v-for="item in room.items">
								<div class="st-px-2 st-py-1 st-w-12 st-text-center st-text-sm st-font-light">
									<button class="st-ml-1 st-px-1 st-font-light st-text-sm st-bg-transparent hover:st-bg-transparent focus:st-bg-transparent st-text-gray-300 hover:st-text-red-500" @click="removeItem(room, item)">
										<font-awesome-icon :icon="times" />
									</button>
								</div>
								<div class="st-px-2 st-py-1 st-w-3/4 st-text-left st-text-sm st-font-light">{{ item.label }}</div>
								<div class="st-px-2 st-py-1 st-w-24 st-text-center st-text-sm st-font-light">
									<div>{{ item.volume[units] }}<span v-text="units === 'metric' ? 'm' : 'ft'"></span><sup>3</sup></div>
								</div>
								<div class="st-px-2 st-py-1 st-w-24 st-text-center st-text-sm st-font-light">
									<input class="st-appearance-none st-border st-rounded st-w-14 st-py-2 st-px-3" :class="[textDarker]" placeholder="Qty" v-bind="changeQtyAttrs(item)" v-on="changeQtyEvents(item)">
								</div>
								<div class="st-px-2 st-py-1 st-w-24 st-text-center st-text-sm st-font-light">
									<div>{{ item.totalVolume[units] }}<span v-text="units === 'metric' ? 'm' : 'ft'"></span><sup>3</sup></div>
								</div>
							</div>
						</slide-down-transition-group>
						<div class="st-mt-2 st-flex st-items-center">
							<div class="st-text-sm st-mr-2" :class="[textDark]">Add Item to {{ room.name }}:</div>
							<div class="st-relative st-flex-1">
								<select class="st-block st-appearance-none st-w-full st-border st-h-8 st-py-3 st-px-4 st-pr-8 st-rounded" :class="[bgLight, borderLighter, textDarker]" v-bind="addItemAttrs" v-on="addItemEvents(room)">
									<option value=""></option>
									<option v-for="item in filteredItems(room.items, items)" :value="JSON.stringify(item)" :key="item.label">{{ item.label }}</option>
								</select>
								<div class="st-pointer-events-none st-absolute st-inset-y-0 st-right-0 st-flex st-items-center st-px-2" :class="[textDarker]">
									<font-awesome-icon :icon="chevronDown" />
								</div>
							</div>
						</div>
					</div>
				</slide-down-transition-group>
				<slide-down-transition>
					<div class="st-shadow" v-if="totalVolume()[units] > 0">
						<div class="st-rounded-t st-p-4" :class="[bgLight]">
							<div class="st-flex st-items-end st-justify-between" :class="[textDark]">
								<div class="st-text-lg">Total Volume:</div>
								<div class="st-text-lg">
									<div>{{ totalVolume()[units] }}<span v-text="units === 'metric' ? 'm' : 'ft'"></span><sup>3</sup></div>
								</div>
							</div>
						</div>
						<slide-down-transition>
							<div v-if="recommendedSpaces.length > 0">
								<slide-down-transition-group>
									<div class="st-border-t st-border-gray-500 st-p-4" :key="space.description" :class="[bgLight, index == recommendedSpaces.length - 1 ? 'st-rounded-b' : '']" v-for="(space, index) in recommendedSpaces">
										<div class="st-flex st-items-end st-justify-between">
											<div class="st-text-base">{{ space.label }}</div>
											<div class="st-text-xs" v-show="space.description">{{ space.description }}</div>
										</div>
										<div class="st-mt-2 st-flex st-items-end st-justify-between">
											<div>
												<div class="st-text-sm">Volume: 
													<div v-if="units === 'metric'">
														{{ space.volume }}m<sup>3</sup>
													</div>
													<div v-if="units === 'imperial'">
														{{ Math.round(space.volume * 35.3147 * 10) / 10 }}ft<sup>3</sup>
													</div>
												</div>
												<div class="st-text-sm">Area: 
													<div v-if="units === 'metric'">
														{{ space.area }}m<sup>2</sup>
													</div>
													<div v-if="units === 'imperial'">
														{{ Math.round(space.area * 10.7639 * 10) / 10 }}ft<sup>3</sup>
													</div>
												</div>
											</div>
											<div class="st-flex st-items-end st-justify-around">
												<a :href="space.enquireUrl" class="st-font-light st-py-2 st-px-4 st-rounded" :class="[secondaryButtonBg, secondaryButtonBgHover, secondaryButtonText, space.bookUrl ? 'st-mr-4' : '']" v-show="space.enquireUrl">Enquire Now</a>
												<a :href="space.bookUrl" class="st-font-light st-py-2 st-px-4 st-rounded" :class="[tertiaryButtonBg, tertiaryButtonBgHover, tertiaryButtonText]" v-show="space.bookUrl">Book Now</a>
											</div>
										</div>
									</div>
								</slide-down-transition-group>
							</div>
						</slide-down-transition>
						<slide-down-transition>
							<div v-if="recommendedSpaces.length == 0">
								<div class="st-border-t st-border-gray-500 st-p-4 st-rounded-b" :class="[bgLight]">
									<slot>Please contact us to discuss your requirements.</slot>
								</div>
							</div>
						</slide-down-transition>
					</div>
				</slide-down-transition>
			</div>
		</div>
	</spacetimator>
</template>

<script>
	import spacetimator from '../spacetimator'
	import SlideDownTransition from './slide-down-transition'
	import SlideDownTransitionGroup from './slide-down-transition-group'
	import { library } from '@fortawesome/fontawesome-svg-core'
	import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons'
	import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

	library.add(faChevronDown)
	library.add(faTimes)

	export default {
		props: {
			recomendations: {
				type: Number,
				default: 3
			},
			units: {
				type: String,
				default: 'metric',
				validator: function (value) {
					return ['metric', 'imperial'].indexOf(value) !== -1
				}
			},
			textDark: {
				type: String,
				default: 'st-text-gray-600'
			},
			textDarker: {
				type: String,
				default: 'st-text-gray-700'
			},
			bgLight: {
				type: String,
				default: 'st-bg-gray-300'
			},
			bgLighter: {
				type: String,
				default: 'st-bg-gray-200'
			},
			bgLightest: {
				type: String,
				default: 'st-bg-gray-100'
			},
			borderLighter: {
				type: String,
				default: 'st-border-gray-200'
			},
			primaryButtonBg: {
				type: String,
				default: 'st-bg-blue-600'
			},
			primaryButtonBgHover: {
				type: String,
				default: 'hover:st-bg-blue-700'
			},
			primaryButtonText: {
				type: String,
				default: 'st-text-white'
			},
			secondaryButtonBg: {
				type: String,
				default: 'st-bg-gray-600'
			},
			secondaryButtonBgHover: {
				type: String,
				default: 'hover:st-bg-gray-700'
			},
			secondaryButtonText: {
				type: String,
				default: 'st-text-white'
			},
			tertiaryButtonBg: {
				type: String,
				default: 'st-bg-green-500'
			},
			tertiaryButtonBgHover: {
				type: String,
				default: 'hover:st-bg-green-600'
			},
			tertiaryButtonText: {
				type: String,
				default: 'st-text-white'
			},
			items: {
				type: Array,
				required: true
			},
			spaces: {
				type: Array,
				required: true
			}
		},
		data() {
			return {
				rooms: [
					{
						name: 'Living Room',
						items: []
					}
				],
			}
		},
		computed: {
			chevronDown () {
				return faChevronDown
			},
			times () {
				return faTimes
			},
		},
		methods: {
			filteredItems(roomItems, allItems) {
				return allItems.filter(ai => roomItems.find(ri => ri.label === ai.label) === undefined)
			},
		},
		components: {
			spacetimator,
			SlideDownTransition,
			SlideDownTransitionGroup,
			FontAwesomeIcon
		}
	}
</script>

<style>
  @tailwind base;
	@tailwind components;
	@tailwind utilities;
  [v-cloak] {
    display: none;
  }
</style>